import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import Chart from 'chart.js/auto';

const RetirementCalculator = () => {
  // Input states with defaults
  const [currentAge, setCurrentAge] = useState(35);
  const [currentMonthlyExpenses, setCurrentMonthlyExpenses] = useState(50000);
  const [currentInvestments, setCurrentInvestments] = useState(1000000);
  const [inflationBeforeRetirement, setInflationBeforeRetirement] = useState(7);
  const [returnRateBeforeRetirement, setReturnRateBeforeRetirement] = useState(12);
  const [retirementAge, setRetirementAge] = useState(60);
  const [otherAnnualExpenses, setOtherAnnualExpenses] = useState(100000);
  const [returnForCurrentInvestments, setReturnForCurrentInvestments] = useState(14);
  const [inflationPostRetirement, setInflationPostRetirement] = useState(6);
  const [returnRateAfterRetirement, setReturnRateAfterRetirement] = useState(8);
  const [incomePercentage, setIncomePercentage] = useState(10);

  const [results, setResults] = useState(null);
  const [simulationResults, setSimulationResults] = useState(null);

  const lifeExpectancy = 85;

  // Input validation helper
  const isValidInput = () => {
    if (
      currentAge < 0 ||
      retirementAge <= currentAge ||
      currentMonthlyExpenses < 0 ||
      currentInvestments < 0 ||
      inflationBeforeRetirement < 0 ||
      returnRateBeforeRetirement < 0 ||
      otherAnnualExpenses < 0 ||
      returnForCurrentInvestments < 0 ||
      inflationPostRetirement < 0 ||
      returnRateAfterRetirement < 0 ||
      incomePercentage < 0
    ) {
      return false;
    }
    return true;
  };

  // Main calculation function
  const calculateRetirement = () => {
    if (!isValidInput()) {
      alert('Please enter valid positive numbers and ensure retirement age is greater than current age.');
      return;
    }

    const yearsToRetirement = retirementAge - currentAge;
    const monthsToRetirement = yearsToRetirement * 12;

    // Overestimate expenses by 10% safety margin
    const overestimatedExpenses = currentMonthlyExpenses * 1.1;

    // Value of current investments at retirement (compounded annually)
    const investmentAtRetirement =
      currentInvestments * Math.pow(1 + returnForCurrentInvestments / 100, yearsToRetirement);

    // Annual expenses at retirement adjusted for inflation (post-retirement inflation)
    const annualExpensesAtRetirement =
      currentMonthlyExpenses * 12 * Math.pow(1 + inflationPostRetirement / 100, yearsToRetirement);

    // Total corpus needed at retirement to cover expenses until life expectancy (simple approach)
    const retirementYears = lifeExpectancy - retirementAge;
    const requiredCorpus = annualExpensesAtRetirement * retirementYears;

    // Corpus to accumulate by fresh investments
    const corpusToAccumulate = Math.max(requiredCorpus - investmentAtRetirement, 0);

    // Monthly return rate during accumulation phase
    const monthlyReturnRate = returnRateBeforeRetirement / 100 / 12;

    // Monthly investment required to accumulate corpusToAccumulate using FV of annuity formula
    let monthlyInvestment = 0;
    if (monthlyReturnRate === 0) {
      monthlyInvestment = corpusToAccumulate / monthsToRetirement;
    } else {
      monthlyInvestment =
        (corpusToAccumulate * monthlyReturnRate) /
        (Math.pow(1 + monthlyReturnRate, monthsToRetirement) - 1);
    }

    // Simulation using income percentage method
    const totalIncomeNeeded = currentMonthlyExpenses * 12 * retirementYears;
    const monthlyContributionRequired = totalIncomeNeeded * (incomePercentage / 100);

    setResults({
      overestimatedExpenses,
      investmentAtRetirement,
      annualExpensesAtRetirement,
      requiredCorpus,
      corpusToAccumulate,
      monthlyInvestment,
    });

    setSimulationResults({
      totalIncomeNeeded,
      monthlyContributionRequired,
    });
  };

  // Data for investment growth chart
  const chartData = {
    labels: Array.from({ length: retirementAge - currentAge }, (_, i) => currentAge + i + 1),
    datasets: [
      {
        label: 'Projected Investment Value',
        data: Array.from({ length: retirementAge - currentAge }, (_, i) =>
          currentInvestments * Math.pow(1 + returnForCurrentInvestments / 100, i + 1)
        ),
        borderColor: 'rgba(54, 162, 235, 0.8)',
        backgroundColor: 'rgba(54, 162, 235, 0.4)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (val) => `₹${val.toLocaleString()}` },
      },
    },
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) => `₹${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <div className="retirement-calculator section-padding bg-light" style={{ padding: '2rem' }}>
      <div className="container-fluid">
        <div className="row gy-4">
          {/* Input Form */}
          <div className="col-lg-4 col-md-6">
            <div className="card shadow-sm p-4 h-100">
              <h2 className="mb-4 text-center">Retirement Planner</h2>
              <p className="text-center mb-4 text-muted">
                Estimate your retirement corpus and monthly investments needed.
              </p>

              {[
                { label: 'Current Age', value: currentAge, setter: setCurrentAge, min: 0 },
                {
                  label: 'Current Monthly Expenses (₹)',
                  value: currentMonthlyExpenses,
                  setter: setCurrentMonthlyExpenses,
                  min: 0,
                },
                {
                  label: 'Current Investments (₹)',
                  value: currentInvestments,
                  setter: setCurrentInvestments,
                  min: 0,
                },
                {
                  label: 'Inflation before Retirement (%)',
                  value: inflationBeforeRetirement,
                  setter: setInflationBeforeRetirement,
                  min: 0,
                },
                {
                  label: 'Expected Rate of Return before Retirement (%)',
                  value: returnRateBeforeRetirement,
                  setter: setReturnRateBeforeRetirement,
                  min: 0,
                },
                { label: 'Retirement Age', value: retirementAge, setter: setRetirementAge, min: 0 },
                {
                  label: 'Other Annual Expenses (₹)',
                  value: otherAnnualExpenses,
                  setter: setOtherAnnualExpenses,
                  min: 0,
                },
                {
                  label: 'Return for Current Investments (%)',
                  value: returnForCurrentInvestments,
                  setter: setReturnForCurrentInvestments,
                  min: 0,
                },
                {
                  label: 'Inflation Post-Retirement (%)',
                  value: inflationPostRetirement,
                  setter: setInflationPostRetirement,
                  min: 0,
                },
                {
                  label: 'Expected Rate of Return after Retirement (%)',
                  value: returnRateAfterRetirement,
                  setter: setReturnRateAfterRetirement,
                  min: 0,
                },
                {
                  label: 'Income Percentage for Simulation (%)',
                  value: incomePercentage,
                  setter: setIncomePercentage,
                  min: 0,
                },
              ].map(({ label, value, setter, min }, idx) => (
                <div className="mb-3" key={idx}>
                  <label className="form-label" htmlFor={`input-${idx}`}>
                    {label}
                  </label>
                  <input
                    type="number"
                    id={`input-${idx}`}
                    className="form-control"
                    min={min}
                    value={value}
                    onChange={(e) => setter(Number(e.target.value))}
                  />
                </div>
              ))}

              <button className="btn btn-primary w-100 mt-3" onClick={calculateRetirement}>
                Calculate
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="col-lg-8 col-md-6">
            <div className="card shadow-sm p-4 h-100">
              {results ? (
                <>
                  <h3 className="mb-4 text-center">Results</h3>

                  {/* Chart */}
                  <div style={{ height: '300px', marginBottom: '2rem' }}>
                    <Line data={chartData} options={chartOptions} />
                  </div>

                  {/* Results Table */}
                  <table className="table table-striped">
                    <tbody>
                      <tr>
                        <td>Overestimated Monthly Expenses</td>
                        <td>₹ {results.overestimatedExpenses.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td>Current Investment Value at Retirement</td>
                        <td>₹ {results.investmentAtRetirement.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td>Annual Expenses at Retirement</td>
                        <td>₹ {results.annualExpensesAtRetirement.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td>Corpus Needed at Retirement</td>
                        <td>₹ {results.requiredCorpus.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td>Corpus to Accumulate (excluding current investments)</td>
                        <td>₹ {results.corpusToAccumulate.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td>Required Monthly Investment</td>
                        <td>₹ {results.monthlyInvestment.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Simulation */}
                  <h4 className="mt-5 mb-3">Simulation Based on Income Percentage</h4>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <td>Total Income Needed over Retirement Period</td>
                        <td>₹ {simulationResults.totalIncomeNeeded.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                      <tr>
                        <td>Monthly Contribution Required ({incomePercentage}%)</td>
                        <td>₹ {simulationResults.monthlyContributionRequired.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="text-center text-muted">
                  Enter your details and click <strong>Calculate</strong> to see results.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;
